import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ReviewBadge, reviewOf, fmtReviewDate } from "../lib/review";
import { useBrand } from "../context/BrandContext";
import ImageDropzone from "../components/ImageDropzone";
import JubelioBadge from "../components/JubelioBadge";
import ComboBox from "../components/ComboBox";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash, CaretDown } from "@phosphor-icons/react";

const EMPTY = {
    brand: "hoshizora",
    name: "", name_jp: "", sku: "", category: "Ring", collection: "", status: "active",
    material: "", stone_type: "", stone_shape: "", stone_grade: "", stone_color: "",
    carat: "", color: "", size: "",
    description: "", product_knowledge: "", internal_notes: "",
    retail_price: 0, stock: 0,
    region: null, product_brand: "", collections: [],
    shopee_url: "", tokopedia_url: "", website_url: "", content_url: "", video_url: "",
    images: [], variants: [],
};

// Defaults applied only when creating a NEW Hoshizora product. Not retro-filled
// onto existing records and never applied to Heels & Tea.
const HOSHIZORA_NEW_DEFAULTS = {
    material: "Silver 925",
    stone_type: "Moissanite",
    stone_grade: "VVS1",
    stone_color: "D Color",
};

export default function ProductForm() {
    const { id } = useParams();
    const isEdit = Boolean(id);
    const [loaded, setLoaded] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    // Where to go on Back / after Save. Entry sources explicitly set state:
    //   - Products list row click → `/products?...filters...`
    //   - Catalog → Product Detail → Edit → `/products/{id}` (detail page)
    // When nothing is provided (deep link, refresh), the operational
    // workspace is the sensible default.
    const returnTo = location.state?.returnTo || "/products";
    const [searchParams] = useSearchParams();
    const { user } = useAuth();
    const { current: activeBrand, brands } = useBrand();
    const [cats, setCats] = useState([]);
    const [form, setForm] = useState({ ...EMPTY, brand: activeBrand });
    const dirty = Boolean(loaded) && JSON.stringify(form) !== JSON.stringify(loaded);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [extraOpen, setExtraOpen] = useState(false);

    const showJewelryFields = form.brand === "hoshizora";
    const isHeelsAndTea = form.brand === "heelsandtea";
    const [structure, setStructure] = useState({ groups: [], featured: [] });

    useEffect(() => {
        const brandToUse = isEdit ? form.brand : activeBrand;
        if (brandToUse !== "heelsandtea") {
            setStructure({ groups: [], featured: [] });
            return;
        }
        (async () => {
            try {
                const { data } = await api.get("/catalog/structure", { params: { brand: brandToUse } });
                setStructure({
                    groups: (data.groups || []).filter((g) => !g.archived),
                    featured: (data.featured || []).filter((f) => !f.archived),
                });
            } catch {
                setStructure({ groups: [], featured: [] });
            }
        })();
    }, [isEdit, activeBrand, form.brand]);

    const regions = structure.groups.filter((g) => g.kind === "region");
    const collectionGroups = structure.groups.filter((g) => g.kind === "collection");

    useEffect(() => {
        (async () => {
            const brandToUse = isEdit ? form.brand : activeBrand;
            try {
                const { data } = await api.get("/categories", { params: { brand: brandToUse } });
                const active = (data || []).filter((c) => !c.archived);
                setCats(active);
                // If current form.category isn't valid for this brand, pick the first one.
                if (!isEdit) {
                    const preselectedRegion = searchParams.get("region");
                    setForm((f) => ({
                        ...f,
                        brand: activeBrand,
                        category: active.find((c) => c.name === f.category)?.name || active[0]?.name || "",
                        region: preselectedRegion || f.region,
                        // Apply Hoshizora combobox defaults only on CREATE — never retro-fill edits.
                        ...(activeBrand === "hoshizora" ? HOSHIZORA_NEW_DEFAULTS : {}),
                    }));
                }
            } catch {}
            if (isEdit) {
                try {
                    const res = await api.get(`/products/${id}`);
                    setForm({ ...EMPTY, ...res.data });
                    setLoaded({ ...EMPTY, ...res.data });
                } catch {
                    toast.error("Product not found");
                    navigate("/catalog");
                }
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isEdit, activeBrand]);

    // Hoshizora combobox options (material/stone_* lists). Grow organically with
    // any new values typed by users on the form.
    const [fieldOptions, setFieldOptions] = useState({
        material: [], stone_type: [], stone_grade: [], stone_color: [], stone_shape: [],
    });
    useEffect(() => {
        if (form.brand !== "hoshizora") return;
        (async () => {
            try {
                const { data } = await api.get("/products/field-options", { params: { brand: "hoshizora" } });
                setFieldOptions({
                    material: data.material || [],
                    stone_type: data.stone_type || [],
                    stone_grade: data.stone_grade || [],
                    stone_color: data.stone_color || [],
                    stone_shape: data.stone_shape || [],
                });
            } catch {}
        })();
    }, [form.brand]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const addVariant = () =>
        set("variants", [
            ...(form.variants || []),
            { id: crypto.randomUUID(), sku: "", label: "", carat: "", retail_price: 0, stock: 0 },
        ]);

    const updateVariant = (i, k, v) => {
        const arr = [...form.variants];
        arr[i] = { ...arr[i], [k]: v };
        set("variants", arr);
    };

    const removeVariant = (i) => set("variants", form.variants.filter((_, idx) => idx !== i));

    const [linking, setLinking] = useState(null);
    const linkVariant = async (i) => {
        setLinking(i);
        try {
            const { data } = await api.get("/jubelio/variant-lookup", { params: { sku: form.variants[i].sku } });
            const arr = [...form.variants];
            arr[i] = { ...arr[i], sku: data.jubelio_item_code, jubelio_item_id: data.jubelio_item_id, jubelio_item_code: data.jubelio_item_code, stock: data.stock, sync_status: "synced" };
            set("variants", arr);
            toast.success(`Linked to Jubelio · ${data.location_code} stock ${data.stock}`);
        } catch (ex) {
            toast.error(formatApiError(ex));
        } finally {
            setLinking(null);
        }
    };
    const unlinkVariant = (i) => {
        const arr = [...form.variants];
        arr[i] = { ...arr[i], jubelio_item_id: null, jubelio_item_code: null, sync_status: null };
        set("variants", arr);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            const payload = {
                ...form,
                retail_price: Number(form.retail_price) || 0,
                stock: Number(form.stock) || 0,
                variants: (form.variants || [])
                    .filter((v) => (v.sku || "").trim() || (v.label || "").trim() || (v.carat || "").trim() || v.size || Number(v.retail_price) || Number(v.stock))
                    .map((v) => ({
                    ...v,
                    retail_price: Number(v.retail_price) || 0,
                    stock: Number(v.stock) || 0,
                })),
            };
            if (isEdit) {
                await api.put(`/products/${id}`, payload);
                toast.success("Product updated");
                navigate(returnTo);
            } else {
                const { data } = await api.post("/products", payload);
                toast.success("Product created");
                navigate(returnTo);
            }
        } catch (ex) {
            setErr(formatApiError(ex?.response?.data?.detail) || "Failed to save");
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={onSubmit} className="animate-fade-up space-y-6 max-w-4xl w-full" data-testid="product-form">
            <Link to={returnTo} data-testid="product-form-back" className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-sage-dark">
                <ArrowLeft size={14} weight="light" /> Back
            </Link>
            <div className="flex items-end justify-between gap-4">
                <div>
                    <div className="eyebrow">{isEdit ? "Edit" : "New"}</div>
                    <h1 className="font-serif text-3xl md:text-5xl mt-1">
                        {isEdit ? "Edit Product" : "Add Product"}
                    </h1>
                </div>
                <button
                    type="submit"
                    disabled={busy}
                    data-testid="save-product-button"
                    className="bg-sage-dark text-white px-5 py-2.5 rounded-sm text-xs uppercase tracking-widest hover:bg-sage-deep disabled:opacity-60"
                >
                    {busy ? "Saving…" : "Save"}
                </button>
            </div>

            {err && <div className="text-sm text-red-600" data-testid="form-error">{err}</div>}

            <Section title="Product Information">
                <Grid>
                    {user?.role === "owner" && (
                        <Field label="Brand *">
                            <select required value={form.brand} onChange={(e) => set("brand", e.target.value)} className={inp} data-testid="field-brand">
                                {brands.map((b) => (
                                    <option key={b.id} value={b.id}>{b.name}</option>
                                ))}
                            </select>
                        </Field>
                    )}
                    <Field label="Product Name *">
                        <input required value={form.name} onChange={(e) => set("name", e.target.value)} className={inp} data-testid="field-name" />
                    </Field>
                    <Field label={<>SKU * <JubelioBadge product={form} /></>}>
                        <input required value={form.sku} onChange={(e) => set("sku", e.target.value)} className={`${inp} font-mono`} data-testid="field-sku" />
                    </Field>
                    <Field label="Category *">
                        <select required value={form.category} onChange={(e) => set("category", e.target.value)} className={inp} data-testid="field-category">
                            {form.category === "" && (
                                <option value="" disabled>— Select a category —</option>
                            )}
                            {cats.map((c) => (
                                <option key={c.id} value={c.name}>{c.name}</option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Status">
                        <select value={form.status} onChange={(e) => set("status", e.target.value)} className={inp} data-testid="field-status">
                            <option value="active">Active</option>
                            <option value="draft">Draft</option>
                            <option value="archived">Archived</option>
                        </select>
                        <p className="text-[11px] text-neutral-500 mt-1">Draft products stay hidden from the public catalog until activated.</p>
                    </Field>
                    {isHeelsAndTea && (
                        <>
                            <Field label="Region">
                                <select value={form.region || ""} onChange={(e) => set("region", e.target.value || null)} className={inp} data-testid="field-region">
                                    <option value="">—</option>
                                    {regions.map((g) => (
                                        <option key={g.id} value={g.slug}>{g.icon} {g.name}</option>
                                    ))}
                                </select>
                                <p className="text-[11px] text-neutral-500 mt-1">Primary country / region. Leave blank for cross-regional items.</p>
                            </Field>
                            <Field label="Product Brand">
                                <input
                                    value={form.product_brand || ""}
                                    onChange={(e) => set("product_brand", e.target.value)}
                                    placeholder="e.g. EVE-A, Gucci, Dior"
                                    className={inp}
                                    data-testid="field-product-brand"
                                />
                                <p className="text-[11px] text-neutral-500 mt-1">Manufacturer brand. Enables featured brand filters (e.g. EVE-A under Japan).</p>
                            </Field>
                            {collectionGroups.length > 0 && (
                                <Field label="Collections">
                                    <div className="flex flex-wrap gap-1.5 mt-1" data-testid="field-collections">
                                        {collectionGroups.map((g) => {
                                            const checked = (form.collections || []).includes(g.slug);
                                            return (
                                                <button
                                                    key={g.id}
                                                    type="button"
                                                    onClick={() => {
                                                        const cur = form.collections || [];
                                                        const next = checked ? cur.filter((x) => x !== g.slug) : [...cur, g.slug];
                                                        set("collections", next);
                                                    }}
                                                    data-testid={`collection-toggle-${g.slug}`}
                                                    className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest border transition-colors ${
                                                        checked ? "bg-sage-dark text-white border-sage-dark" : "border-mist text-neutral-500 hover:border-sage-dark"
                                                    }`}
                                                >
                                                    {g.icon} {g.name}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <p className="text-[11px] text-neutral-500 mt-1">A product can appear in Winter + its Region without duplication.</p>
                                </Field>
                            )}
                        </>
                    )}
                    {showJewelryFields && (
                        <Field label="Carat">
                            <input value={form.carat} onChange={(e) => set("carat", e.target.value)} placeholder="e.g. 0.5 CT or 0.5 - 2 CT" className={inp} data-testid="field-carat" />
                        </Field>
                    )}
                    <Field label={<>Retail Price (Rp) * <JubelioBadge product={form} /></>}>
                        <input type="number" min="0" required value={form.retail_price} onChange={(e) => set("retail_price", e.target.value)} className={inp} data-testid="field-price" />
                    </Field>
                    <Field label={<>Current Stock <JubelioBadge product={form} /></>}>
                        <input type="number" min="0" value={form.stock} onChange={(e) => set("stock", e.target.value)} className={inp} data-testid="field-stock" />
                    </Field>
                </Grid>
                <Field label="Short Description" className="mt-4">
                    <textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className={inp} placeholder="A short product description (1–3 sentences)" data-testid="field-description" />
                </Field>
            </Section>

            <Section title="Product Photos">
                <ImageDropzone images={form.images || []} onChange={(imgs) => set("images", imgs)} />
            </Section>

            <Section
                title="Variants"
                subtitle={showJewelryFields ? "If this product has size or carat variants, manage them here." : "If this product has size or style variants, manage them here."}
                action={
                    <button type="button" onClick={addVariant} className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-neutral-600 hover:text-sage-dark" data-testid="add-variant">
                        <Plus size={12} weight="bold" /> Add Variant
                    </button>
                }
            >
                {form.variants?.length ? (
                    <div className="space-y-3 md:space-y-2">
                        {/* Desktop header row — labels mirror the inline per-row mobile labels for consistency */}
                        <div className={`hidden md:grid gap-2 text-[10px] uppercase tracking-widest text-neutral-400 px-1 ${showJewelryFields ? "grid-cols-12" : "grid-cols-11"}`}>
                            <span className={showJewelryFields ? "col-span-3" : "col-span-4"}>Variant / Size</span>
                            <span className="col-span-3">SKU</span>
                            {showJewelryFields && <span className="col-span-2">Carat</span>}
                            <span className="col-span-2">Retail Price (Rp)</span>
                            <span className="col-span-1">Stock</span>
                            <span className="col-span-1" aria-hidden />
                        </div>
                        {form.variants.map((v, i) => (
                            <div
                                key={v.id || i}
                                className="border border-mist rounded-sm p-3 md:p-0 md:border-0 bg-[#FBFAF7] md:bg-transparent"
                                data-testid={`variant-row-${i}`}
                            >
                                {/* Mobile stacked layout */}
                                <div className="md:hidden space-y-3">
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="eyebrow">Variant {i + 1}</span>
                                        <button type="button" onClick={() => removeVariant(i)} className="text-neutral-400 hover:text-red-600" aria-label="Delete variant" data-testid={`variant-remove-${i}`}>
                                            <Trash size={16} weight="light" />
                                        </button>
                                    </div>
                                    <StackedField label="Variant / Size">
                                        <input placeholder={showJewelryFields ? "e.g. Size 6 or 1 CT" : "e.g. Size M or Color: Black"} value={v.label} onChange={(e) => updateVariant(i, "label", e.target.value)} className={inp} data-testid={`variant-label-${i}`} />
                                    </StackedField>
                                    <StackedField label="SKU">
                                        <input placeholder="SKU" value={v.sku} onChange={(e) => updateVariant(i, "sku", e.target.value)} className={`${inp} font-mono`} data-testid={`variant-sku-${i}`} />
                                    </StackedField>
                                    {showJewelryFields && (
                                        <StackedField label="Carat">
                                            <input placeholder="e.g. 0.5" value={v.carat || ""} onChange={(e) => updateVariant(i, "carat", e.target.value)} className={inp} data-testid={`variant-carat-${i}`} />
                                        </StackedField>
                                    )}
                                    <StackedField label="Retail Price (Rp)">
                                        <input type="number" inputMode="numeric" min="0" placeholder="0" value={v.retail_price} onChange={(e) => updateVariant(i, "retail_price", e.target.value)} className={inp} data-testid={`variant-price-${i}`} />
                                    </StackedField>
                                    <StackedField label="Stock">
                                        <input type="number" inputMode="numeric" min="0" placeholder="0" value={v.stock} readOnly={v.jubelio_item_id != null} onChange={(e) => updateVariant(i, "stock", e.target.value)} className={`${inp} ${v.jubelio_item_id != null ? "bg-neutral-100 text-neutral-500" : ""}`} data-testid={`variant-stock-${i}`} />
                                    </StackedField>
                                </div>

                                {/* Desktop grid layout */}
                                <div className={`hidden md:grid gap-2 items-center ${showJewelryFields ? "grid-cols-12" : "grid-cols-11"}`}>
                                    <input placeholder={showJewelryFields ? "e.g. Size 6" : "e.g. Size M"} value={v.label} onChange={(e) => updateVariant(i, "label", e.target.value)} className={`${inp} ${showJewelryFields ? "col-span-3" : "col-span-4"}`} />
                                    <input placeholder="SKU" value={v.sku} onChange={(e) => updateVariant(i, "sku", e.target.value)} className={`${inp} col-span-3 font-mono`} />
                                    {showJewelryFields && (
                                        <input placeholder="Carat" value={v.carat || ""} onChange={(e) => updateVariant(i, "carat", e.target.value)} className={`${inp} col-span-2`} />
                                    )}
                                    <input type="number" placeholder="Price" value={v.retail_price} onChange={(e) => updateVariant(i, "retail_price", e.target.value)} className={`${inp} col-span-2`} />
                                    <input type="number" placeholder="Stock" value={v.stock} readOnly={v.jubelio_item_id != null} title={v.jubelio_item_id != null ? "Synced from Jubelio" : undefined} onChange={(e) => updateVariant(i, "stock", e.target.value)} className={`${inp} col-span-1 ${v.jubelio_item_id != null ? "bg-neutral-100 text-neutral-500" : ""}`} />
                                    <button type="button" onClick={() => removeVariant(i)} className="col-span-1 text-neutral-400 hover:text-red-600 py-2 justify-self-end" aria-label="Delete variant">
                                        <Trash size={16} weight="light" />
                                    </button>
                                </div>
                                <VariantJubelio v={v} i={i} busy={linking === i} onLink={linkVariant} onUnlink={unlinkVariant} />
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-sm text-neutral-500">No variants. This product will be treated as a single SKU.</p>
                )}
            </Section>

            <section className="bg-white border border-mist rounded-sm">
                <button
                    type="button"
                    onClick={() => setExtraOpen((o) => !o)}
                    data-testid="toggle-info-tambahan"
                    aria-expanded={extraOpen}
                    className="w-full flex items-center justify-between p-5 md:p-6 text-left"
                >
                    <div>
                        <h2 className="font-serif text-2xl tracking-tight">Additional Info</h2>
                        <p className="text-xs text-neutral-500 mt-1">
                            {showJewelryFields
                                ? "Material, stone details, selling points, marketplace links, internal notes."
                                : "Status, selling points, marketplace links, Instagram, internal notes."}
                        </p>
                    </div>
                    <CaretDown
                        size={20}
                        weight="light"
                        className={`shrink-0 text-neutral-500 transition-transform ${extraOpen ? "rotate-180" : ""}`}
                    />
                </button>
                {extraOpen && (
                    <div className="border-t border-mist p-5 md:p-8 space-y-6" data-testid="info-tambahan-panel">
                        <Grid>
                            {showJewelryFields && (
                                <>
                                    <Field label="Material">
                                        <ComboBox
                                            value={form.material || ""}
                                            options={fieldOptions.material}
                                            onChange={(v) => set("material", v)}
                                            placeholder="Select material…"
                                            testId="field-material"
                                        />
                                    </Field>
                                    <Field label="Stone Type">
                                        <ComboBox
                                            value={form.stone_type || ""}
                                            options={fieldOptions.stone_type}
                                            onChange={(v) => set("stone_type", v)}
                                            placeholder="Select stone type…"
                                            testId="field-stone-type"
                                        />
                                    </Field>
                                    <Field label="Stone Grade">
                                        <ComboBox
                                            value={form.stone_grade || ""}
                                            options={fieldOptions.stone_grade}
                                            onChange={(v) => set("stone_grade", v)}
                                            placeholder="Select stone grade…"
                                            testId="field-stone-grade"
                                        />
                                    </Field>
                                    <Field label="Stone Color">
                                        <ComboBox
                                            value={form.stone_color || ""}
                                            options={fieldOptions.stone_color}
                                            onChange={(v) => set("stone_color", v)}
                                            placeholder="Select stone color…"
                                            testId="field-stone-color"
                                        />
                                    </Field>
                                    <Field label="Stone Cut">
                                        <ComboBox
                                            value={form.stone_shape || ""}
                                            options={fieldOptions.stone_shape}
                                            onChange={(v) => set("stone_shape", v)}
                                            placeholder="Select stone cut…"
                                            testId="field-stone-cut"
                                        />
                                    </Field>
                                </>
                            )}
                        </Grid>

                        <Field label="Product Knowledge / Selling Points">
                            <textarea rows={3} value={form.product_knowledge} onChange={(e) => set("product_knowledge", e.target.value)} className={inp} data-testid="field-product-knowledge" />
                        </Field>

                        <div>
                            <div className="eyebrow mb-2">Marketplace Links</div>
                            <Grid>
                                <Field label="Shopee"><input value={form.shopee_url} onChange={(e) => set("shopee_url", e.target.value)} className={inp} placeholder="https://shopee.co.id/…" data-testid="field-shopee" /></Field>
                                <Field label="Tokopedia"><input value={form.tokopedia_url} onChange={(e) => set("tokopedia_url", e.target.value)} className={inp} placeholder="https://tokopedia.com/…" data-testid="field-tokopedia" /></Field>
                                {showJewelryFields && (
                                    <Field label="Website"><input value={form.website_url} onChange={(e) => set("website_url", e.target.value)} className={inp} data-testid="field-website" /></Field>
                                )}
                                <Field label="Instagram / Content"><input value={form.content_url} onChange={(e) => set("content_url", e.target.value)} className={inp} data-testid="field-content" /></Field>
                                {showJewelryFields && (
                                    <Field label="Video URL"><input value={form.video_url} onChange={(e) => set("video_url", e.target.value)} className={inp} placeholder="https://…" data-testid="field-video" /></Field>
                                )}
                            </Grid>
                        </div>

                        <Field label="Internal Notes">
                            <textarea rows={2} value={form.internal_notes} onChange={(e) => set("internal_notes", e.target.value)} className={inp} data-testid="field-internal-notes" />
                        </Field>
                    </div>
                )}
            </section>
            {isEdit && form.id && (
                <AdminReview product={form} dirty={dirty} isOwner={user?.role === "owner"}
                    onChange={(d) => { setForm((f) => ({ ...f, review_status: d.review_status, checked_by_name: d.checked_by_name, checked_at: d.checked_at })); setLoaded((l) => ({ ...l, review_status: d.review_status, checked_by_name: d.checked_by_name, checked_at: d.checked_at })); }} />
            )}
        </form>
    );
}

function AdminReview({ product, dirty, isOwner, onChange }) {
    const [busy, setBusy] = useState(false);
    const s = reviewOf(product);
    const act = async (action) => {
        setBusy(true);
        try {
            const { data } = await api.post(`/products/${product.id}/review`, { action });
            onChange(data);
            toast.success(action === "check" ? "Marked as checked" : "Reset to To Check");
        } catch (ex) {
            toast.error(formatApiError(ex));
        } finally {
            setBusy(false);
        }
    };
    return (
        <section className="border border-mist rounded-sm bg-white p-4 md:p-5 flex flex-wrap items-center justify-between gap-3" data-testid="admin-review-section">
            <div>
                <div className="eyebrow">Admin Review</div>
                <div className="mt-1 text-sm font-sans" data-testid="admin-review-status">
                    {s === "CHECKED"
                        ? <span className="text-[#3F6B4E] font-medium">✓ Checked by {product.checked_by_name}<span className="block text-xs text-neutral-500 font-normal">{fmtReviewDate(product.checked_at, true)}</span></span>
                        : <ReviewBadge product={product} />}
                </div>
                {dirty && s !== "CHECKED" && <div className="text-[11px] text-neutral-500 mt-1">Save your changes before marking as checked.</div>}
            </div>
            <div className="flex items-center gap-2">
                {isOwner && s !== "TO_CHECK" && (
                    <button type="button" disabled={busy} onClick={() => act("reset")} data-testid="review-reset-button"
                        className="border border-mist px-3 py-2 rounded-sm text-xs uppercase tracking-widest text-neutral-600 hover:border-sage-dark disabled:opacity-40">Reset to To Check</button>
                )}
                {s !== "CHECKED" && (
                    <button type="button" disabled={busy || dirty} onClick={() => act("check")} data-testid="review-check-button"
                        className="bg-sage-dark text-white px-4 py-2 rounded-sm text-xs uppercase tracking-widest hover:bg-sage-deep disabled:opacity-40">✓ Mark as checked</button>
                )}
            </div>
        </section>
    );
}

const inp = "w-full border border-mist rounded-sm px-3 py-2 bg-white text-base md:text-sm focus:outline-none focus:border-sage-dark transition-colors";

function VariantJubelio({ v, i, busy, onLink, onUnlink }) {
    if (v.jubelio_item_id != null) {
        return (
            <div className="mt-2 md:mt-1 flex flex-wrap items-center gap-2 text-[11px] text-neutral-600 font-sans" data-testid={`variant-jubelio-linked-${i}`}>
                <JubelioBadge mapped size={12} />
                <span>Stock synced from Jubelio · <span className="font-mono">{v.jubelio_item_code}</span></span>
                <button type="button" onClick={() => onUnlink(i)} className="uppercase tracking-widest text-neutral-400 hover:text-red-600" data-testid={`variant-jubelio-unlink-${i}`}>Unlink</button>
            </div>
        );
    }
    return (
        <div className="mt-2 md:mt-1 text-[11px] font-sans">
            <button type="button" disabled={busy || !(v.sku || "").trim()} onClick={() => onLink(i)}
                className="uppercase tracking-widest text-neutral-500 hover:text-sage-dark disabled:opacity-40"
                data-testid={`variant-jubelio-link-${i}`}>
                {busy ? "Linking…" : "Link SKU to Jubelio"}
            </button>
        </div>
    );
}

function StackedField({ label, children }) {
    return (
        <label className="block">
            <span className="eyebrow block mb-1">{label}</span>
            {children}
        </label>
    );
}

function Section({ title, subtitle, action, children }) {
    return (
        <section className="bg-white border border-mist rounded-sm p-5 md:p-8">
            <div className="flex items-start justify-between mb-5 gap-3">
                <div>
                    <h2 className="font-serif text-2xl tracking-tight">{title}</h2>
                    {subtitle && <p className="text-xs text-neutral-500 mt-1">{subtitle}</p>}
                </div>
                {action}
            </div>
            {children}
        </section>
    );
}
function Grid({ children }) {
    return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;
}
function Field({ label, children, className = "" }) {
    return (
        <label className={`block ${className}`}>
            <span className="eyebrow flex items-center gap-1">{label}</span>
            <div className="mt-1">{children}</div>
        </label>
    );
}
